import { ApiService } from '../apiService'
import { isReviewModeOn } from '@features/reviewMode/reviewModeStore'
import type {
  UserProfileDto,
  UpdateUserProfileDto,
} from '../../types/profile'

// Current location types
export interface UpdateCurrentLocationRequest {
  latitude: number
  longitude: number
  accuracy?: number
  timestamp?: string
  /**
   * Ground speed in m/s when the browser knows it. The server treats "inside a geofence and
   * not moving" as evidence of a real visit, which lets a place with a short dwell complete
   * on one report instead of waiting to be sampled twice.
   *
   * `coords.speed` is null on most desktop browsers and on any fix that isn't from GPS.
   * Leave it undefined there — the server reads unknown as no evidence, never as stopped.
   */
  speed?: number
  /**
   * Moderator review mode (Backlog 058). Filled from the review-mode store when the caller leaves
   * it out; the server honours it only for Moderator/Admin.
   */
  reviewMode?: boolean
}

/** A single silent adventure surfaced by a location check (auto join/complete). */
export interface SilentAdventureResult {
  adventureId: string
  title: string
  minutesRemaining?: number | null
  minutesElapsed?: number | null
  justJoined: boolean
  justCompleted: boolean
  collectionId?: string | null
  collectionName?: string | null
  collectionProgressPercent?: number | null
}

/** Silent adventures evaluated against the location sent with this update. */
export interface SilentAdventureCheckResponse {
  joinedAdventures: SilentAdventureResult[]
  completedAdventures: SilentAdventureResult[]
  inProgressAdventures: SilentAdventureResult[]
}

export interface CurrentLocationResponse {
  success: boolean
  updatedAt: string
  // Populated when this location update auto-joined/completed silent adventures.
  silentAdventures?: SilentAdventureCheckResponse
}

export interface UserLocationDto {
  userId: string
  latitude: number
  longitude: number
  accuracy?: number
  timestamp: string
}

// Request type for setting avatar/cover from media
export interface SetProfileMediaRequest {
  mediaId: string
}

export const profileApi = {
  // ====================
  // USER PROFILE
  // ====================

  // Get current user's profile
  getMyProfile: () => ApiService.get<UserProfileDto>('/api/me/profile'),

  // Update current user's profile
  updateMyProfile: (data: UpdateUserProfileDto) =>
    ApiService.post<UserProfileDto>('/api/me/profile', data),

  // ====================
  // AVATAR & COVER PHOTO
  // ====================

  // Set avatar from existing media file
  setAvatar: (mediaId: string) =>
    ApiService.put<UserProfileDto>('/api/me/profile/avatar', { mediaId }),

  // Clear avatar image
  clearAvatar: () =>
    ApiService.delete<UserProfileDto>('/api/me/profile/avatar'),

  // Set cover photo from existing media file
  setCoverPhoto: (mediaId: string) =>
    ApiService.put<UserProfileDto>('/api/me/profile/cover', { mediaId }),

  // Clear cover photo
  clearCoverPhoto: () =>
    ApiService.delete<UserProfileDto>('/api/me/profile/cover'),

  // Get user profile by ID
  getUserProfile: (id: string) => ApiService.get<UserProfileDto>(`/api/users/${id}/profile`),


  // ====================
  // CURRENT LOCATION
  // ====================

  // Update current user's real-time location
  // reviewMode is stamped here, at the one funnel, so no caller can forget it (Backlog 058).
  updateCurrentLocation: (data: UpdateCurrentLocationRequest) =>
    ApiService.put<CurrentLocationResponse>('/api/me/current-location', {
      ...data,
      reviewMode: data.reviewMode ?? isReviewModeOn(),
    }),

  // Get current user's last reported location
  getMyCurrentLocation: () =>
    ApiService.get<UserLocationDto>('/api/me/current-location'),

  // Get a user's last reported location (for friends/group members)
  getUserLocation: (userId: string) =>
    ApiService.get<UserLocationDto>(`/api/users/${userId}/location`),

  // Get locations of multiple users (friends, group members, etc.)
  getUsersLocations: (userIds: string[]) =>
    ApiService.post<UserLocationDto[]>('/api/users/locations', { userIds }),
}
